/**
 * Predicts what a value save does to one row, by the server's rule: with
 * e-Manifest written the row is Fix pending; otherwise it is Resolved only if
 * vendor data, the manifest document and e-Manifest all agree after the writes.
 */
import type { ResolutionTargetLiteral, ResolveRow } from "./resolveWithValue.helpers";

import { documentDisagrees, normalizeForCompare } from "./resolveWithValue.values";

export type RowOutcome = "RESOLVED" | "PENDING" | "STAYS_OPEN";
export type CopyKey = "vendor" | "eManifest" | "document";

export type OutcomeRow = Pick<
  ResolveRow,
  "field" | "eManifestValue" | "vendorValue" | "manifestDocumentValue" | "documentDiscrepant"
>;

function differs(field: string, current: string | null | undefined, answerValue: string): boolean {
  return normalizeForCompare(field, current ?? "") !== normalizeForCompare(field, answerValue);
}

function vendorLeftDisagreeing(row: OutcomeRow, answerValue: string, written: boolean): boolean {
  // A null vendor value is "no vendor data", not a disagreement.
  if (written || row.vendorValue == null) return false;
  return differs(row.field, row.vendorValue, answerValue);
}

function documentLeftDisagreeing(
  row: OutcomeRow,
  answerValue: string,
  written: boolean,
  eManifestWritten: boolean
): boolean {
  if (written || row.manifestDocumentValue == null) return false;
  // e-Manifest keeps its value, so the persisted verdict applies. Once it is
  // rewritten that verdict is stale and the document is compared to the answer.
  if (
    !eManifestWritten &&
    normalizeForCompare(row.field, row.eManifestValue ?? "") ===
      normalizeForCompare(row.field, answerValue)
  ) {
    return documentDisagrees(row);
  }
  return differs(row.field, row.manifestDocumentValue, answerValue);
}

/**
 * The copies that still differ from the answer after the writes.
 * e-Manifest only appears when it is not being written.
 */
export function copiesLeftDisagreeing(
  row: OutcomeRow,
  answerValue: string | null,
  applyTo: readonly ResolutionTargetLiteral[]
): CopyKey[] {
  if (answerValue == null) return [];
  const eManifestWritten = applyTo.includes("EMANIFEST");
  const left: CopyKey[] = [];
  if (!eManifestWritten && differs(row.field, row.eManifestValue, answerValue)) {
    left.push("eManifest");
  }
  if (vendorLeftDisagreeing(row, answerValue, applyTo.includes("VENDOR_DATA"))) left.push("vendor");
  if (documentLeftDisagreeing(row, answerValue, applyTo.includes("DOCUMENT"), eManifestWritten)) {
    left.push("document");
  }
  return left;
}

export function predictRowOutcome(
  row: OutcomeRow,
  answerValue: string | null,
  applyTo: readonly ResolutionTargetLiteral[]
): RowOutcome {
  if (applyTo.includes("EMANIFEST")) return "PENDING";
  return copiesLeftDisagreeing(row, answerValue, applyTo).length === 0 ? "RESOLVED" : "STAYS_OPEN";
}

export interface OutcomeCounts {
  resolved: number;
  pending: number;
  stayOpen: number;
}

/** The status rows land in after save: one name when they all agree, else counts ("1 Resolved · 1 Fix pending"). */
export function formatStatusAfterSave(counts: OutcomeCounts): string {
  const parts: [number, string][] = [
    [counts.resolved, "Resolved"],
    [counts.pending, "Fix pending"],
    [counts.stayOpen, "Pending Review"],
  ];
  const present = parts.filter(([n]) => n > 0);
  if (present.length === 0) return "No change";
  if (present.length === 1) return present[0][1];
  return present.map(([n, label]) => `${n} ${label}`).join(" · ");
}

/** Counts each row's predicted outcome. */
export function countOutcomes(outcomes: readonly RowOutcome[]): OutcomeCounts {
  return {
    resolved: outcomes.filter((o) => o === "RESOLVED").length,
    pending: outcomes.filter((o) => o === "PENDING").length,
    stayOpen: outcomes.filter((o) => o === "STAYS_OPEN").length,
  };
}
